if (!customElements.get('dusk-perfume-sequence')) {
    customElements.define('dusk-perfume-sequence', class extends HTMLElement {
      connectedCallback() {
        if (this.ready) return;
        this.ready = true;
        this.canvas = this.querySelector('canvas');
        this.stage = this.querySelector('.dusk-sequence__stage');
        this.button = this.querySelector('button');
        this.manifest = JSON.parse(this.querySelector('.dusk-sequence__manifest').textContent);
        this.context = this.canvas.getContext('2d', { alpha: false });
        this.mobile = matchMedia('(max-width: 749px)');
        this.reduceMotion = matchMedia('(prefers-reduced-motion: reduce)');
        this.connection = navigator.connection;
        this.abort = new AbortController();
        this.generation = 0;
        this.raf = 0;
        this.displayProgress = null;
        this.frame = 0;
        this.targetFrame = 0;
        this.lastFrame = -1;
        this.direction = 1;
        this.near = false;
        this.requestTick = this.requestTick.bind(this);
        this.tick = this.tick.bind(this);
        this.geometryValues = new Map();
        this.onScroll = () => { this.scrollGeometryNeeded = true; this.requestTick(); };
        this.resize = () => { this.measureNeeded = true; this.lastFrame = -1; this.requestTick(); };
        const signal = this.abort.signal;
        document.addEventListener('scroll', this.onScroll, { capture: true, passive: true, signal });
        window.addEventListener('scroll', this.onScroll, { passive: true, signal });
        window.addEventListener('resize', this.resize, { passive: true, signal });
        window.addEventListener('pageshow', this.resize, { signal });
        window.visualViewport?.addEventListener('resize', this.resize, { passive: true, signal });
        document.addEventListener('visibilitychange', () => { if (!document.hidden) this.resize(); }, { signal });
        this.mobile.addEventListener('change', () => this.configure(), { signal });
        this.reduceMotion.addEventListener('change', () => this.configure(), { signal });
        this.connection?.addEventListener?.('change', () => this.configure(), { signal });
        this.button?.addEventListener('click', () => { this.motionOptIn = true; this.configure(); }, { signal });
        this.resizeObserver = new ResizeObserver(this.resize);
        this.resizeObserver.observe(this);
        const obstructions = document.querySelectorAll('.gta-widget.gta-bar, #PBarNextFrameWrapper, .header-section');
        obstructions.forEach(el => this.resizeObserver.observe(el));
        if (typeof MutationObserver !== 'undefined') {
          this.headerObserver = new MutationObserver(this.resize);
          obstructions.forEach(el => this.headerObserver.observe(el, { attributes: true, attributeFilter: ['style', 'class', 'hidden'] }));
        }
        this.configure();
      }
      findScrollRoot() {
        for (let el = this.parentElement; el && el !== document.body; el = el.parentElement) {
          if (/(auto|scroll)/.test(getComputedStyle(el).overflowY) && el.clientHeight > 0) return el;
        }
        return null;
      }
      configure() {
        const enabled = this.dataset.scrollScrub !== 'false';
        const limited = this.reduceMotion.matches || this.connection?.saveData;
        this.active = enabled && (!limited || this.motionOptIn) && !!this.context;
        this.button.hidden = !enabled || !limited || this.active;
        this.toggleAttribute('data-scroll-active', this.active);
        this.measureNeeded = true;
        this.displayProgress = null;
        this.lastFrame = -1;
        this.scrollRoot = this.findScrollRoot();
        this.observer?.disconnect();
        this.observer = new IntersectionObserver(entries => {
          this.near = entries[0].isIntersecting;
          if (this.active && this.near) { this.startLoading(); this.requestTick(); }
        }, { root: this.scrollRoot, rootMargin: '500px 0px' });
        this.observer.observe(this);
        this.resetMedia();
        this.measure();
        const rect = this.getBoundingClientRect();
        this.targetFrame = this.frame = Math.round(this.progressFor(rect.top) * (this.media.frames - 1));
        this.near = rect.top < window.innerHeight + 500 && rect.bottom > this.scrollportTop - 500;
        if (this.active && this.near) this.startLoading();
        this.requestTick();
      }
      resetMedia() {
        this.generation++;
        this.downloadAbort?.abort();
        this.downloads?.forEach(controller => controller.abort());
        this.downloadAbort = new AbortController();
        this.quickBitmap?.close?.();
        this.quickBitmap = null;
        this.cache?.forEach(bitmap => bitmap.close?.());
        this.cache = new Map();
        this.decoding = new Set();
        this.blobs = new Map();
        this.failed = new Set();
        this.downloads = new Map();
        this.media = this.manifest[this.mobile.matches ? 'mobile' : 'desktop'];
        this.loading = false;
        this.firstFrameShown = false;
        this.quickLoading = false;
        this.lastDrawKey = null;
        this.lastRawProgress = null;
        this.lastProgressStyle = null;
        this.removeAttribute('data-frame-visible');
      }
      setGeometry(name, value) {
        if (this.geometryValues.get(name) === value) return;
        this.geometryValues.set(name, value);
        this.style.setProperty(name, value);
      }
      progressFor(top) {
        return Math.max(0, Math.min(1, (this.pinViewportTop - top) / this.travel));
      }
      checkScrollport() {
        this.scrollGeometryNeeded = false;
        if (!this.scrollRoot) return;
        const rect = this.scrollRoot.getBoundingClientRect();
        if (Math.abs(rect.top - this.rootTop) > 1 || Math.abs(rect.bottom - this.rootBottom) > 1) this.measureNeeded = true;
      }
      measure() {
        this.measureNeeded = false;
        this.measuredWidth = this.getBoundingClientRect().width;
        this.viewportHeight = window.innerHeight;
        const scrollRoot = this.findScrollRoot();
        if (scrollRoot !== this.scrollRoot) this.scrollRoot = scrollRoot;
        const rootRect = this.scrollRoot?.getBoundingClientRect();
        this.rootTop = rootRect?.top;
        this.rootBottom = rootRect?.bottom;
        if (this.scrollRoot) this.resizeObserver.observe(this.scrollRoot);
        this.scrollportTop = rootRect ? Math.max(0, rootRect.top) : 0;
        const screenHeight = this.mobile.matches ? (this.stableHeight || window.innerHeight) : window.innerHeight;
        if (!this.mobile.matches || this.stableWidth !== this.measuredWidth || !this.stableHeight || Math.abs(window.innerHeight - this.stableHeight) > 160) {
          this.stableHeight = window.innerHeight;
          this.stableWidth = this.measuredWidth;
        }
        let bottom = this.mobile.matches ? this.stableHeight : Math.min(rootRect ? rootRect.bottom : screenHeight, window.visualViewport?.height || screenHeight);
        const preview = document.querySelector('#PBarNextFrameWrapper');
        if (preview) {
          const rect = preview.getBoundingClientRect();
          if (rect.height && rect.bottom >= window.innerHeight - 2 && rect.top > window.innerHeight / 2) bottom = Math.min(bottom, rect.top);
        }
        let pin = this.scrollportTop;
        document.querySelectorAll('.gta-widget.gta-bar, .header-section').forEach(el => {
          const css = getComputedStyle(el);
          const rect = el.getBoundingClientRect();
          if ((css.position === 'fixed' || css.position === 'sticky') && css.display !== 'none' && css.visibility !== 'hidden' && rect.height > 0) {
            if (el.classList.contains('header-section')) pin = Math.max(pin, this.scrollportTop + Math.max(0, parseFloat(css.top) || 0) + rect.height);
            else if (rect.top < window.innerHeight / 2 && rect.bottom > this.scrollportTop) pin = Math.max(pin, rect.bottom);
          }
        });
        this.pinOffset = Math.max(0, pin - this.scrollportTop);
        this.pinViewportTop = this.scrollportTop + this.pinOffset;
        this.stageHeight = Math.max(180, bottom - this.pinViewportTop);
        this.travel = Math.max(400, this.stageHeight * (Number(this.mobile.matches ? this.dataset.distanceMobile : this.dataset.distanceDesktop) || 200) / 100);
        const rect = this.getBoundingClientRect();
        const fit = this.fitFrame(rect.width, this.stageHeight);
        this.setGeometry('--dusk-pin-offset', this.pinOffset + 'px');
        this.setGeometry('--dusk-stage-height', this.stageHeight + 'px');
        this.setGeometry('--dusk-travel', this.travel + 'px');
        this.setGeometry('--dusk-frame-width', fit.width + 'px');
        this.setGeometry('--dusk-frame-height', fit.height + 'px');
        this.setGeometry('--dusk-frame-x', fit.x + 'px');
        this.setGeometry('--dusk-frame-y', fit.y + 'px');
        const ratio = Math.min(window.devicePixelRatio || 1, 2, 1920 / Math.max(1, rect.width), Math.max(1, 1 / fit.scale));
        const width = Math.round(rect.width * ratio), height = Math.round(this.stageHeight * ratio);
        if (this.canvas.width !== width || this.canvas.height !== height) {
          this.canvas.width = width;
          this.canvas.height = height;
          this.lastFrame = -1;
        }
        this.drawFit = this.fitFrame(width, height);
        // A static colour backdrop avoids a full-canvas blur pass on every frame.
        this.backdrop = this.context.createLinearGradient?.(0, 0, 0, height);
        this.backdrop?.addColorStop(0, '#bba58f');
        this.backdrop?.addColorStop(.55, '#c4a88a');
        this.backdrop?.addColorStop(1, '#cbb395');
      }
      startLoading() {
        if (this.loading || !this.active) return;
        this.loading = true;
        this.loadQuick();
        this.pump();
      }
      async loadQuick() {
        if (this.quickLoading || !this.media.quick) return;
        this.quickLoading = true;
        const generation = this.generation;
        const url = this.media.quick.url;
        try {
          const warmup = window.duskQuickWarmup;
          const blob = await (warmup?.url === url ? warmup.promise : fetch(url, {
            signal: this.downloadAbort.signal, cache: 'force-cache', priority: 'high',
            headers: { Accept: 'image/webp,image/*;q=0.8' }
          }).then(response => { if (!response.ok) throw new Error('Reveal unavailable'); return response.blob(); }));
          const bitmap = await createImageBitmap(blob);
          if (generation !== this.generation || !this.ready) { bitmap.close(); return; }
          this.quickBitmap = bitmap;
          this.lastFrame = -1;
          this.requestTick();
        } catch { }
      }
      loadOrder() {
        const target = Math.floor(this.targetFrame / this.media.tiles);
        const current = Math.floor(this.frame / this.media.tiles);
        const order = [target, current, target + this.direction, current + this.direction, target - this.direction, current - this.direction];
        if (this.firstFrameShown || this.quickBitmap) {
          // Store compressed frames progressively; decode only the rolling local window.
          for (let distance = 2; distance < this.media.urls.length; distance++) {
            order.push(target + distance * this.direction, target - distance * this.direction);
          }
        }
        return [...new Set(order)].filter(index => index >= 0 && index < this.media.urls.length);
      }
      pump() {
        if (!this.loading || !this.active || !this.near || document.hidden) return;
        const order = this.loadOrder();
        const target = order[0];
        if (!this.blobs.has(target) && !this.failed.has(target) && !this.downloads.has(target) && this.downloads.size >= 2) {
          const current = Math.floor(this.frame / this.media.tiles);
          const candidates = [...this.downloads.keys()].filter(index => index !== current && Math.abs(index - target) > 2);
          const index = candidates.sort((a, b) => Math.abs(b - target) - Math.abs(a - target))[0];
          if (index !== undefined) {
            const controller = this.downloads.get(index);
            this.downloads.delete(index);
            controller.abort();
          }
        }
        for (const index of order) {
          if (this.downloads.size >= 2) break;
          if (this.blobs.has(index) || this.downloads.has(index) || this.failed.has(index)) continue;
          const generation = this.generation;
          const controller = new AbortController();
          this.downloads.set(index, controller);
          const warmup = window.duskRevealWarmup;
          const transfer = warmup?.url === this.media.urls[index] && warmup.promise
            ? warmup.promise
            : fetch(this.media.urls[index], { signal: controller.signal, cache: 'force-cache', priority: index === target ? 'high' : 'low', headers: { Accept: 'image/webp,image/*;q=0.8' } })
              .then(response => { if (!response.ok) throw new Error('Frame unavailable'); return response.blob(); });
          transfer
            .then(blob => {
              if (generation !== this.generation || !this.ready || controller.signal.aborted) return;
              this.blobs.set(index, blob);
              this.requestTick();
            })
            .catch(error => { if (generation === this.generation && !controller.signal.aborted && error.name !== 'AbortError') this.failed.add(index); })
            .finally(() => {
              if (generation !== this.generation || !this.ready || this.downloads.get(index) !== controller) return;
              this.downloads.delete(index);
              this.pump();
            });
        }
      }
      async decode(index) {
        if (!this.blobs.has(index) || this.failed.has(index) || this.cache.has(index) || this.decoding.has(index) || this.decoding.size >= 1) return;
        const generation = this.generation;
        this.decoding.add(index);
        let bitmap;
        try {
          bitmap = await createImageBitmap(this.blobs.get(index));
          if (generation !== this.generation || !this.ready) { bitmap.close(); return; }
          if (Math.abs(index - Math.floor(this.frame / this.media.tiles)) > 1) { bitmap.close(); return; }
          this.cache.set(index, bitmap);
          this.trimCache();
          if (index === Math.floor(this.frame / this.media.tiles)) this.lastFrame = -1;
          this.requestTick();
        } catch {
          if (generation === this.generation) this.failed.add(index);
        } finally {
          if (generation === this.generation) { this.decoding.delete(index); this.requestTick(); }
        }
      }
      trimCache() {
        const target = Math.floor(this.frame / this.media.tiles);
        while (this.cache.size > 3) {
          const distant = [...this.cache.keys()].sort((a, b) => Math.abs(b - target) - Math.abs(a - target))[0];
          this.cache.get(distant).close?.();
          this.cache.delete(distant);
        }
      }
      fitFrame(width, height) {
        const fw = this.media.width, fh = this.media.height;
        const [left, top, right, bottom] = this.media.bounds;
        const margin = Math.min(16, width * .025);
        const cover = Math.max(width / fw, height / fh);
        const safe = Math.min((width - margin * 2) / (right - left), (height - margin * 2) / (bottom - top));
        const scale = Math.min(cover, safe);
        const drawWidth = fw * scale, drawHeight = fh * scale;
        const x = Math.max(margin - left * scale, Math.min(width - margin - right * scale, (width - drawWidth) / 2));
        const y = Math.max(margin - top * scale, Math.min(height - margin - bottom * scale, (height - drawHeight) / 2));
        return { x, y, width: drawWidth, height: drawHeight, scale };
      }
      drawFrame(frame) {
        const index = Math.floor(frame / this.media.tiles);
        const sharp = this.cache.get(index);
        if (!sharp) this.decode(index);
        const bitmap = sharp || this.quickBitmap;
        if (!bitmap) return;
        const media = sharp ? this.media : this.media.quick;
        const tile = sharp ? frame % this.media.tiles : Math.round(frame / (this.media.frames - 1) * (media.frames - 1));
        const key = sharp ? 'sharp-' + frame : 'quick-' + tile;
        if (this.lastDrawKey === key && this.lastFrame !== -1) return;
        const ctx = this.context, width = this.canvas.width, height = this.canvas.height;
        const sx = tile % media.columns * media.width;
        const sy = Math.floor(tile / media.columns) * media.height;
        const fit = this.drawFit || this.fitFrame(width, height);
        ctx.fillStyle = this.backdrop || '#c4a88a';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(bitmap, sx, sy, media.width, media.height, fit.x, fit.y, fit.width, fit.height);
        this.lastFrame = frame;
        this.lastDrawKey = key;
        if (!this.firstFrameShown) { this.firstFrameShown = true; this.pump(); }
        this.setAttribute('data-frame-visible', '');
        this.dataset.renderQuality = sharp ? 'sharp' : 'quick';
        this.dataset.renderFrame = String(frame);
      }
      requestTick() {
        if (!this.active || this.raf || document.hidden) return;
        this.raf = requestAnimationFrame(this.tick);
      }
      tick(now) {
        this.raf = 0;
        if (!this.active || document.hidden) return;
        if (this.scrollGeometryNeeded) this.checkScrollport();
        if (this.measureNeeded) this.measure();
        const progress = this.progressFor(this.getBoundingClientRect().top);
        this.targetFrame = Math.round(progress * (this.media.frames - 1));
        if (this.displayProgress === null) this.displayProgress = progress;
        const delta = progress - this.displayProgress;
        if (this.lastRawProgress !== null && Math.abs(progress - this.lastRawProgress) > .0005) this.direction = Math.sign(progress - this.lastRawProgress);
        this.lastRawProgress = progress;
        const elapsed = Math.min(64, Math.max(1, now - (this.lastTick || now - 16)));
        this.lastTick = now;
        this.displayProgress += delta * (1 - Math.exp(-elapsed / 45));
        if (Math.abs(progress - this.displayProgress) < .0005) this.displayProgress = progress;
        this.frame = Math.round(this.displayProgress * (this.media.frames - 1));
        const progressStyle = this.displayProgress.toFixed(4);
        if (this.lastProgressStyle !== progressStyle) { this.style.setProperty('--dusk-progress', progressStyle); this.lastProgressStyle = progressStyle; }
        if (this.near) this.startLoading();
        if (this.loading && this.near) {
          this.pump();
          const atlas = Math.floor(this.frame / this.media.tiles);
          this.drawFrame(this.frame);
          this.decode(atlas + this.direction);
          if (Math.abs(this.targetFrame - this.frame) < this.media.tiles) this.decode(atlas - this.direction);
        }
        if (this.displayProgress !== progress) this.requestTick();
      }
      disconnectedCallback() {
        this.ready = false;
        this.generation++;
        cancelAnimationFrame(this.raf);
        this.raf = 0;
        this.abort?.abort();
        this.downloadAbort?.abort();
        this.downloads?.forEach(controller => controller.abort());
        this.downloads?.clear();
        this.observer?.disconnect();
        this.resizeObserver?.disconnect();
        this.headerObserver?.disconnect();
        this.quickBitmap?.close?.();
        this.quickBitmap = null;
        this.cache?.forEach(bitmap => bitmap.close?.());
        this.cache?.clear();
        this.blobs?.clear();
      }
    });
  }


